import React, { useEffect, useState } from "react";
import type {
  HeadersFunction,
  LinksFunction,
  LoaderArgs } from
"@remix-run/node";
import { json } from "@remix-run/node";
import { useLoaderData, useSearchParams } from "@remix-run/react";
import { createAuthClient } from "better-auth/react";
import { posthog } from "~/lib/posthog";
import sfnLogo from "~/static/sfn_logo.png";
import sfnStyles from "~/styles/sfn-design-system.css";
import creatorJoinStyles from "~/styles/content-reward-join-login.css";
import {
  buildAccountLinkCompleteUrl,
  CREATOR_NOT_LINKED_MESSAGE,
  getCreatorIdFromRedirectTo,
  getHandleClaimCreatorId,
  resolvePostLoginDestination,
  sanitizeRedirectTo } from
"~/lib/auth-redirect";
import { isCollabInviteReturn } from "~/lib/collab-invite-apply-step";
import { getContentRewardJoinRedirectContext } from "~/lib/content-reward-join-observability";
import { trackContentRewardJoin } from "~/lib/content-reward-join-observability.server";
import { recordJoinAttemptStage } from "~/lib/content-reward-join-attempts.server";
import {
  getRewardRegistrationContext,
  rewardAuthFetchOptions,
  trackRewardAuthForm } from
"~/lib/content-reward-registration";
import type { QueryResultRow } from "pg";
import { query } from "~/components/database/db.server";
import { getCreatorIdFromLinkProof } from "~/services/auth.server";
import { getRewardRegistrationFlowVersion } from "~/lib/content-reward-registration-intents.server";

export const links: LinksFunction = () => [
{ rel: "stylesheet", href: sfnStyles },
{ rel: "stylesheet", href: creatorJoinStyles }];


// Auth pages must never be cached — a stale document served for a URL with
// different `redirectTo`/`accountIssue` query params than the one it was
// generated for hydrates against the wrong client render (React error #418).
export const headers: HeadersFunction = () => ({
  "Cache-Control": "no-store"
});

/**
 * Emit the server-visible start of the Better Auth handoff. Client PostHog
 * events alone cannot drive a CloudWatch alarm, and a failure before the auth
 * callback otherwise leaves no server-side terminal signal to compare against
 * the reward_return marker.
 */
export async function loader({ request }: LoaderArgs) {
  const url = new URL(request.url);
  const context = getContentRewardJoinRedirectContext(
    url.searchParams.get("redirectTo")
  );
  if (context) {
    trackContentRewardJoin({
      event: "content_reward_join_better_auth_handoff_started",
      stage: "better_auth_handoff",
      outcome: "started",
      joinAttemptId: context.joinAttemptId,
      rewardId: context.rewardId,
      creatorId: context.creatorId
    });
    void recordJoinAttemptStage({
      attemptId: context.joinAttemptId,
      stage: "login_handoff",
      rewardId: context.rewardId,
      creatorId: context.creatorId
    });
  }

  // Account-first smart default (plan section C): a creator-shaped return path
  // whose canonical creator is ALREADY linked to a Better Auth user defaults to
  // Sign in; otherwise Create account. This is a convenience hint only — a
  // wrong guess never blocks the creator because the other mode remains one
  // click away, and it is NOT a public "does this account exist?" endpoint
  // (the read is server-side and scoped to the creator named by the safe
  // return path).
  const redirectTo = url.searchParams.get("redirectTo");
  const creatorHint = getCreatorIdFromRedirectTo(redirectTo);
  let signinHint = false;
  if (creatorHint) {
    try {
      const row = await query<{id: string;} & QueryResultRow>(
        `SELECT id FROM "user" WHERE creator_id = $1 LIMIT 1`,
        [creatorHint]
      );
      signinHint = row.rows.length > 0;
    } catch {
      signinHint = false;
    }
  }
  const accountFirstRewardReturn = creatorHint ?
  await isAccountFirstRewardReturn(request, redirectTo) :
  false;
  return json({ signinHint, accountFirstRewardReturn });
}

const CONTENT_REWARD_BROWSE_PATH = /^\/creator_studio\/content_rewards\/browse\/([^/]+)$/;

/**
 * Personalised account-first reward link (2026-09-29). DM-blast links carry
 * `?creator_id=<handle>` as an attribution hint, and CampaignDetailScreen keeps
 * it in the login return path (E-07). On an account-first (v2) reward the
 * creator must land back on the reward page after sign-up / sign-in and apply
 * there — TikTok only runs later, at Register. Routing through
 * /account-link/complete instead found no TikTok proof and parked them on the
 * "Verify with TikTok" recovery screen.
 *
 * The account-link hop is kept when a verified TikTok link proof is present
 * (the TikTok-first content_reward_join handoff: the proof must be consumed to
 * link the account) and for legacy v1 rewards, which are OAuth-first by design.
 */
async function isAccountFirstRewardReturn(
request: Request,
redirectTo: string | null)
: Promise<boolean> {
  const safe = sanitizeRedirectTo(redirectTo);
  if (!safe) return false;
  const match = CONTENT_REWARD_BROWSE_PATH.exec(new URL(safe, "http://local").pathname);
  if (!match) return false;
  if (getCreatorIdFromLinkProof(request)) return false;
  let rewardId: string;
  try {
    rewardId = decodeURIComponent(match[1]);
  } catch {
    return false;
  }
  return (await getRewardRegistrationFlowVersion(rewardId)) === 2;
}

/**
 * Better Auth React client — calls /api/auth/* on the same origin.
 */
const authClient = createAuthClient();

/**
 * A failed handle claim on an onboarding return: a message for the form, and whether the
 * creator should be switched to the sign-in form (that login belongs to another creator).
 */
class JoinLinkError extends Error {
  constructor(message: string, readonly switchToSignIn = false) {
    super(message);
  }
}

/**
 * Which email to log in with (Tad, 2026-11-04): the masked email of the login that holds
 * the handle ("ja•••@g•••.com"), as `api.onboarding.join-link` answers it, or the generic
 * wording when it has none.
 */
const loginWithHint = (ownerEmailHint: string | null) =>
ownerEmailHint ? `Log in with ${ownerEmailHint}.` : "Log in with the email you used before.";

/** A login that already owns another creator. */
const wrongLoginMessage = (handle: string, ownerEmailHint: string | null) =>
`This isn't the email linked to @${handle}. ${loginWithHint(ownerEmailHint)}`;

/** A sign-up (or unlinked login) on a handle another login already holds. */
const handleTakenMessage = (handle: string, ownerEmailHint: string | null) =>
`@${handle} already has an account. ${loginWithHint(ownerEmailHint)}`;

/**
 * Claims the link's handle for the signed-in account (Tad, 2026-11-02/03). /join's cards and
 * the /insights gate both come back through /login, and this is the one call that links a new
 * login to the creator it was sent for — the same policy /join used to call itself.
 */
async function claimOnboardingHandle(
creatorId: string)
: Promise<{ok: true;} | {ok: false;code: string;message: string;ownerEmailHint: string | null;}> {
  try {
    const response = await fetch("/api/onboarding/join-link", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ creatorId })
    });
    if (response.ok) return { ok: true };
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
      code?: string;
      ownerEmailHint?: unknown;
    };
    return {
      ok: false,
      code: body.code ?? "server_error",
      message: body.error ?? "We could not set up your account. Please try again.",
      ownerEmailHint: typeof body.ownerEmailHint === "string" && body.ownerEmailHint ? body.ownerEmailHint : null
    };
  } catch {
    return {
      ok: false,
      code: "server_error",
      message: "We could not set up your account. Please try again.",
      ownerEmailHint: null
    };
  }
}

/** The signed-in account's own landing page: used when it opens someone else's link. */
async function ownCreatorHome(): Promise<string> {
  try {
    const response = await fetch("/api/me/user-type");
    if (!response.ok) return "/creator_studio";
    const data = (await response.json()) as {creatorHome?: unknown;};
    return typeof data.creatorHome === "string" ? data.creatorHome : "/creator_studio";
  } catch {
    return "/creator_studio";
  }
}

/**
 * Full-screen login page for brands and agencies.
 *
 * Two-panel layout: left side shows brand messaging and value props;
 * right side contains the email/password sign-in form.
 * Designed for desktop — optimised for 1280px+ viewports.
 */
function isCreatorRedirect(redirectTo: string | null): boolean {
  if (!redirectTo) return false;
  // Account-link recovery/completion are creator-only control destinations (their
  // loaders redirect brand managers to /dashboard), so a login carrying one as
  // its return path is a creator login even though the redirectTo itself doesn't
  // name a creator_id (the id is nested inside the encoded returnTo). 2026-09-02
  // account-linking RCA: without this, the primary OnboardingDirectSuccess CTA
  // routed creators to the brand two-panel login page.
  if (
  redirectTo.startsWith("/account-link/complete") ||
  redirectTo.startsWith("/account-link/recovery"))
  {
    return true;
  }
  // Account-first apply (2026-09-15): an anonymous visitor's login return is the
  // creator-neutral content-rewards browse page WITHOUT a creator_id (the
  // account owns the saved application; TikTok runs later). It is still a
  // creator destination — the brand two-panel must not be shown.
  if (redirectTo.startsWith("/creator_studio/content_rewards/browse/")) {
    return true;
  }
  // Collab Invite Pages account-first apply (2026-10-13, planning/
  // 10_13_collab_invite_swap_at_apply.md §10): the invite page and its TikTok
  // completion are creator destinations, so a creator gets the creator
  // sign-up, never the brand two-panel.
  if (isCollabInviteReturn(redirectTo)) {
    return true;
  }
  return (
  redirectTo.startsWith("/profile") ||
  redirectTo.startsWith("/creator_studio") ||
  redirectTo.startsWith("/insights") ||
  redirectTo.startsWith("/join")) &&
  redirectTo.includes("creator_id=");
}

const BRAND_COPY = {
  badge: "For Brands & Agencies",
  heading:
  <>
      TikTok creator intelligence,{" "}
      <span style={{ color: "var(--sfn-lime)" }}>built for scale.</span>
    </>,

  subheading:
  "Discover top-performing angles, monitor creator coherence, and turn short-form content into measurable revenue — all in one platform.",
  valueProps: [
  {
    icon: "📊",
    title: "Real-time coherence monitoring",
    desc: "Track every creator across your roster with automated scoring."
  },
  {
    icon: "🎯",
    title: "Angle intelligence",
    desc: "Identify which hooks and narratives are driving GMV for your brand."
  },
  {
    icon: "⚡",
    title: "Instant content ideas",
    desc: "AI-generated briefs tailored to your brand DNA, ready to brief creators."
  }],

  formSubtitle: (mode: string) =>
  mode === "signup" ?
  "Create your account to continue accessing Short Form Nation." :
  "Sign in to your brand or agency account."
};

const CREATOR_COPY = {
  badge: "For Creators",
  heading:
  <>
      Your content.{" "}
      <span style={{ color: "var(--sfn-lime)" }}>Your performance.</span>
    </>,

  subheading:
  "See exactly how your videos are performing, get personalised content ideas, and understand what's working — so you can do more of it.",
  valueProps: [
  {
    icon: "📈",
    title: "Your performance at a glance",
    desc: "Views, engagement, and GMV broken down by video so you always know what's landing."
  },
  {
    icon: "💡",
    title: "Content ideas made for you",
    desc: "AI-powered briefs based on your niche and top-performing angles."
  },
  {
    icon: "🏆",
    title: "Stay ahead of the brief",
    desc: "See your coherence score and how well your content aligns with the brief before it becomes a problem."
  }],

  formSubtitle: (mode: string) =>
  mode === "signup" ?
  "Create your account to access your creator dashboard." :
  "Sign in to your creator account."
};

export default function Login() {
  const [searchParams] = useSearchParams();
  const loaderData = useLoaderData<{
    signinHint?: boolean;
    accountFirstRewardReturn?: boolean;
  }>();
  const accountFirstRewardReturn = loaderData.accountFirstRewardReturn === true;
  const redirectTo = searchParams.get("redirectTo");
  const contentRewardJoinContext =
  getContentRewardJoinRedirectContext(redirectTo);
  const isContentRewardJoin = contentRewardJoinContext !== null;
  // Account-first reward registration context (browse return path, no
  // src=discord requirement) — drives the Better Auth reward-correlation
  // headers and the account-only form events.
  const registrationContext = getRewardRegistrationContext(redirectTo);
  const accountIssue = searchParams.get("accountIssue");
  const isCreator = isCreatorRedirect(redirectTo);
  const copy = isCreator ? CREATOR_COPY : BRAND_COPY;

  // Creators arrive from a join link (content reward, checkout, onboarding)
  // to create an account — default to sign-up first, with a clear sign-in
  // button below the form. Account-first smart default (plan C): a creator
  // whose canonical creator is ALREADY linked to a Better Auth user defaults
  // to Sign in; otherwise Create account. Brand/agency flows keep sign-in as
  // the default. A wrong guess never blocks the creator — the other mode is
  // one click away. A link that names its form (`?mode=signin|signup`, the
  // /insights gate's two cards) opens that form instead of the guess.
  const modeParam = searchParams.get("mode");
  const [mode, setMode] = useState<"signin" | "signup" | "forgot">(
    modeParam === "signin" || modeParam === "signup" ?
    modeParam :
    isCreator ?
    loaderData.signinHint ?
    "signin" :
    "signup" :
    "signin"
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(() =>
  accountIssue === "creator_not_linked" ? CREATOR_NOT_LINKED_MESSAGE : null
  );
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Post-hydration marker for E2E: this page is reached by a full-page
  // navigation, and on a slow/cold `remix dev` compile a test can fill the
  // form before React attaches the controlled inputs' onChange handlers —
  // hydration then snaps the DOM value back to the empty initial state.
  // `data-hydrated` only flips true from a client effect, so it is false on
  // both the server render and the first client render and never causes a
  // hydration mismatch; a test can wait on it before typing.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    setHydrated(true);
  }, []);

  /**
   * Authenticated fast-path for an onboarding return (2026-11-03). A creator who arrives
   * here already signed in — a second tab, a reload, a reopened link — finishes exactly as
   * a fresh sign-in does: the link's handle is claimed and the page leaves for the
   * destination the flow chose. Gated on either creator-shaped return, because
   * `getCreatorIdFromRedirectTo` ignores `/join` returns.
   */
  useEffect(() => {
    const creatorReturn =
    getCreatorIdFromRedirectTo(redirectTo) ?? getHandleClaimCreatorId(redirectTo);
    if (!creatorReturn) return;
    let cancelled = false;
    (async () => {
      // Client-side session is the ground truth for "already authenticated".
      // Anonymous visitors short-circuit here — no probe, no redirect — so the
      // normal sign-up/sign-in form remains the entry. A throwing getSession
      // is treated as anonymous (the form stays the entry); it must never
      // produce an unhandled rejection that pollutes the login page.
      let session: Awaited<ReturnType<typeof authClient.getSession>> | null = null;
      try {
        session =
        typeof authClient.getSession === "function" ?
        await authClient.getSession() :
        null;
      } catch {
        return;
      }
      const userId = session?.data?.user?.id ?? session?.user?.id;
      if (!userId || cancelled) return;

      try {
        const destination = await getPostLoginDestination(userId, { alreadySignedIn: true });
        if (!cancelled) window.location.href = destination;
      } catch (error) {
        if (!cancelled && error instanceof JoinLinkError) {
          setError(error.message);
          if (error.switchToSignIn) setMode("signin");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Once per return path: the claim is idempotent and the page leaves on success.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectTo, accountFirstRewardReturn]);

  function switchMode(next: "signin" | "signup" | "forgot") {
    setMode(next);
    setError(null);
    setForgotSent(false);
    trackRewardAuthForm(registrationContext, next, "mode_changed");
  }

  // Account-only modal → login handoff: emit the shown event once.
  useEffect(() => {
    trackRewardAuthForm(registrationContext, mode, "shown");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2026-11-03 rollout: a creator who reaches /login from her link and gives up there is
  // invisible otherwise — /join already reported "left for login". Emit the screen once, so
  // the funnel has a step between the cards and the claimed handle.
  useEffect(() => {
    const creatorId = getHandleClaimCreatorId(redirectTo);
    if (!creatorId) return;
    posthog.capture("creator_agent_login_shown", {
      flow: "creator_agent",
      creator_id: creatorId,
      auth_mode: mode === "signup" || mode === "signin" ? mode : "forgot"
    });
    // Once per return path, like the claim it leads to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectTo]);

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await authClient.requestPasswordReset({
        email,
        redirectTo: `${window.location.origin}/reset-password`
      });
      setForgotSent(true);
    } catch {
      setError("Could not send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  /**
   * Attach the creator profile named by the return path to the freshly
   * authenticated account. `/api/link-creator` is the authorization boundary
   * (session + userId match + first-claim-wins); a non-ok response must throw
   * so the failure is shown, never swallowed into a silent redirect.
   */
  /**
   * Determines the post-login destination based on user type (Phase 2 of the
   * cross-browser account-linking RCA):
   *   - A /join or /insights return (the onboarding flows) → claim the link's
   *     handle for this login, then straight to the destination it asked for.
   *     No account-link hop: those flows have no TikTok step (2026-11-02/03).
   *   - A legacy creator-shaped return path → the account-link completion route.
   *     It derives the creator from the signed proof server-side (links via any
   *     same-browser proof, else shows recovery).
   *   - Brand manager → /dashboard (terms gate when required).
   *   - Linked creator → safe requested destination or /creator_studio.
   *   - Unlinked creator → /account-link/complete?returnTo=<safe-destination>.
   * A genuinely unlinked non-brand account is NEVER dropped on /profile.
   */
  async function getPostLoginDestination(userId: string, options?: {alreadySignedIn?: boolean;}): Promise<string> {
    try {
      // Personalised account-first reward link (creator_id is only an
      // attribution hint, see the loader): back to the reward page to apply.
      if (accountFirstRewardReturn) {
        const safe = sanitizeRedirectTo(redirectTo);
        if (safe) return safe;
      }

      // /join and the /insights gate: claim the handle the link names, then land on
      // the destination the flow chose (the feed, or Creator Studio with `src=join`).
      const claimCreatorId = getHandleClaimCreatorId(redirectTo);
      if (claimCreatorId) {
        const claim = await claimOnboardingHandle(claimCreatorId);
        // The join funnel's missing step (2026-11-03 rollout): /join reports that a creator
        // left for /login, and this is the only place that says whether the claim landed and
        // where they then went. Without it the funnel stopped at the login screen.
        if (claim.ok) {
          const destination = sanitizeRedirectTo(redirectTo) ?? "/creator_studio";
          posthog.capture("creator_agent_join_link_claimed", {
            flow: "creator_agent",
            creator_id: claimCreatorId,
            auth_mode: mode === "signup" || mode === "signin" ? mode : "forgot",
            destination: destination.startsWith("/insights") ? "feed" : "studio",
            already_signed_in: Boolean(options?.alreadySignedIn)
          });
          return destination;
        }
        posthog.capture("creator_agent_join_link_failed", {
          flow: "creator_agent",
          creator_id: claimCreatorId,
          reason: claim.code,
          already_signed_in: Boolean(options?.alreadySignedIn)
        });
        if (claim.code === "brand_manager_account") return "/dashboard";
        if (claim.code === "account_linked_to_other_creator") {
          // A session that was already signed in when the page loaded has simply opened
          // someone else's link: send them to their own home, never sign them out.
          if (options?.alreadySignedIn) return await ownCreatorHome();
          // The login is another creator's: never carry on as this one.
          try {
            await authClient.signOut();
          } catch {


            // Nothing to do: the form shows the message either way, and the next log in
            // replaces the session.
          }throw new JoinLinkError(wrongLoginMessage(claimCreatorId, claim.ownerEmailHint), true);}
        if (claim.code === "creator_owned_by_other_account") {
          // Another login already holds this handle: usually the creator signed up again
          // with a new email. That account can never become this creator, so sign it out
          // and open Sign in with the hint to the email that can.
          try {
            await authClient.signOut();
          } catch {

            // As above: the message shows either way.
          }throw new JoinLinkError(handleTakenMessage(claimCreatorId, claim.ownerEmailHint), true);
        }
        throw new JoinLinkError(claim.message);
      }

      if (getCreatorIdFromRedirectTo(redirectTo)) {
        return buildAccountLinkCompleteUrl(
          sanitizeRedirectTo(redirectTo) ?? "/creator_studio"
        );
      }

      // Account-first apply (2026-09-15): a creator-neutral content-rewards
      // browse return (no creator_id — the account owns the saved application,
      // TikTok runs later) lands the signed-in-unlinked creator straight back on
      // the apply page to submit the step-4 registration. No account-link hop:
      // there is no TikTok proof to consume yet.
      if (redirectTo?.startsWith("/creator_studio/content_rewards/browse/")) {
        return sanitizeRedirectTo(redirectTo) ?? "/creator_studio";
      }

      // Collab Invite Pages account-first apply: straight back to the invite
      // page (it saves the application, then offers Connect TikTok) or to the
      // invite TikTok completion. No account-link hop: the invite flow links
      // the account itself once TikTok returns. A brand manager is still sent
      // to /dashboard by the destinations' own guards.
      if (isCollabInviteReturn(redirectTo)) {
        return sanitizeRedirectTo(redirectTo) ?? "/creator_studio";
      }

      const res = await fetch("/api/me/user-type");
      if (!res.ok) return "/dashboard";
      const data = await res.json();
      // UX nicety: send a brand user who must accept the current terms straight
      // to the gate instead of bouncing through /dashboard first. The
      // loader-level gate (requireBrandManagerWithTerms) is the real enforcement.
      if (data.isBrandManager && data.termsAcceptanceRequired) {
        const safe = sanitizeRedirectTo(redirectTo) ?? "/dashboard";
        return `/accept-terms?redirectTo=${encodeURIComponent(safe)}`;
      }
      return resolvePostLoginDestination({
        redirectTo,
        isBrandManager: data.isBrandManager,
        hasLinkedCreator: data.hasLinkedCreator === true,
        creatorHome: typeof data.creatorHome === "string" ? data.creatorHome : null
      });
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error("Could not finish signing in. Please try again.");
    }
  }

  /**
   * A dead end on a creator's login form (2026-11-03 rollout). /join reports "left for
   * login" and nothing else would say the creator never got past the password field; the
   * reason names the cause (bad password, an existing account, a server error).
   */
  function reportCreatorAgentLoginFailure(
  authMode: "signin" | "signup",
  reason: string,
  extra: Record<string, unknown> = {})
  : void {
    const creatorId = getHandleClaimCreatorId(redirectTo);
    if (!creatorId) return;
    posthog.capture("creator_agent_login_failed", {
      flow: "creator_agent",
      creator_id: creatorId,
      auth_mode: authMode,
      reason,
      ...extra
    });
  }

  /**
   * Handle email/password sign-in via Better Auth's client-side API.
   * Brand managers are routed to /dashboard; creators to /profile.
   */
  async function handleEmailSignIn(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await authClient.signIn.email({
        email,
        password,
        ...(registrationContext ?
        { fetchOptions: rewardAuthFetchOptions(registrationContext) } :
        {})
      });
      if (result.data) {
        const destination = await getPostLoginDestination(result.data.user.id);
        const userType = destination.startsWith('/dashboard') ? 'brand_manager' : 'creator';
        // Standardize the browser identity on the opaque Better Auth user ID,
        // not the email (2026-09-11 incident finding 2). Browser events keyed
        // on email and server events keyed on Better Auth user IDs landed on
        // different PostHog persons; both sides now use the opaque account ID
        // so a journey joins on one person. Email stays a property.
        posthog.identify(result.data.user.id, { email, user_type: userType });
        posthog.capture('user_signed_in', { user_type: userType });
        trackRewardAuthForm(registrationContext, "signin", "submitted");
        if (contentRewardJoinContext) {
          posthog.capture(
            "content_reward_join_better_auth_succeeded",
            {
              flow: "content_reward_join",
              auth_mode: "sign_in",
              creator_id: contentRewardJoinContext.creatorId,
              reward_id: contentRewardJoinContext.rewardId,
              join_attempt_id: contentRewardJoinContext.joinAttemptId
            },
            { send_instantly: true }
          );
        }
        window.location.href = destination;
      } else {
        if (contentRewardJoinContext) {
          posthog.capture("content_reward_join_better_auth_failed", {
            flow: "content_reward_join",
            auth_mode: "sign_in",
            creator_id: contentRewardJoinContext.creatorId,
            reward_id: contentRewardJoinContext.rewardId,
            join_attempt_id: contentRewardJoinContext.joinAttemptId,
            reason: "invalid_credentials"
          });
        }
        reportCreatorAgentLoginFailure("signin", "invalid_credentials");
        setError("Invalid email or password. Please try again.");
      }
    } catch (error) {
      if (contentRewardJoinContext) {
        posthog.capture("content_reward_join_better_auth_failed", {
          flow: "content_reward_join",
          auth_mode: "sign_in",
          creator_id: contentRewardJoinContext.creatorId,
          reward_id: contentRewardJoinContext.rewardId,
          join_attempt_id: contentRewardJoinContext.joinAttemptId,
          reason: "sign_in_failed",
          error_name: error instanceof Error ? error.name : "unknown"
        });
      }
      reportCreatorAgentLoginFailure("signin", "sign_in_failed", {
        error_name: error instanceof Error ? error.name : "unknown"
      });
      setError(error instanceof Error ? error.message : "Sign-in failed. Please try again.");
      if (error instanceof JoinLinkError && error.switchToSignIn) setMode("signin");
    } finally {
      setLoading(false);
    }
  }

  /**
   * Handle first-time sign-up. BetterAuth will verify the email is
   * pre-provisioned in the users table before creating the account.
   * Brand managers are routed to /dashboard; creators to /profile.
   */
  async function handleEmailSignUp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // Better Auth requires a `name` on sign-up. Creator join flows no longer
      // collect it as a field — use the email address as the display name.
      const displayName = name.trim() || email;
      const result = await authClient.signUp.email({
        email,
        password,
        name: displayName,
        ...(registrationContext ?
        { fetchOptions: rewardAuthFetchOptions(registrationContext) } :
        {})
      });
      if (result.data) {
        const destination = await getPostLoginDestination(result.data.user.id);
        const userType = destination.startsWith('/dashboard') ? 'brand_manager' : 'creator';
        // Same opaque-account-ID standardization as sign-in (finding 2): the
        // browser person is the Better Auth user ID, email is a property.
        posthog.identify(result.data.user.id, { email, name: displayName, user_type: userType });
        posthog.capture('user_signed_up', { user_type: userType });
        trackRewardAuthForm(registrationContext, "signup", "submitted");
        if (contentRewardJoinContext) {
          posthog.capture(
            "content_reward_join_better_auth_succeeded",
            {
              flow: "content_reward_join",
              auth_mode: "sign_up",
              creator_id: contentRewardJoinContext.creatorId,
              reward_id: contentRewardJoinContext.rewardId,
              join_attempt_id: contentRewardJoinContext.joinAttemptId
            },
            { send_instantly: true }
          );
        }
        window.location.href = destination;
      } else {
        const message = (result.error as any)?.message;
        // Account-first smart default, rule 4: when signup reports an existing
        // account, retain the entered email and switch to sign-in so a returning
        // creator is never stuck on the "account already exists" wall.
        const code = (result.error as any)?.code;
        if (
        code === "USER_ALREADY_EXISTS" ||
        code === "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL")
        {
          trackRewardAuthForm(registrationContext, "signup", "validation_blocked");
          reportCreatorAgentLoginFailure("signup", "account_exists", { error_code: code });
          setMode("signin");
          setError("An account already exists with this email. Sign in to continue.");
          return;
        }
        if (contentRewardJoinContext) {
          posthog.capture("content_reward_join_better_auth_failed", {
            flow: "content_reward_join",
            auth_mode: "sign_up",
            creator_id: contentRewardJoinContext.creatorId,
            reward_id: contentRewardJoinContext.rewardId,
            join_attempt_id: contentRewardJoinContext.joinAttemptId,
            reason: "sign_up_rejected"
          });
        }
        reportCreatorAgentLoginFailure("signup", "sign_up_rejected", { error_code: code });
        setError(message || "Sign-up failed. Please check your details and try again.");
      }
    } catch (err: any) {
      if (contentRewardJoinContext) {
        posthog.capture("content_reward_join_better_auth_failed", {
          flow: "content_reward_join",
          auth_mode: "sign_up",
          creator_id: contentRewardJoinContext.creatorId,
          reward_id: contentRewardJoinContext.rewardId,
          join_attempt_id: contentRewardJoinContext.joinAttemptId,
          reason: "sign_up_failed",
          error_name: err instanceof Error ? err.name : "unknown"
        });
      }
      reportCreatorAgentLoginFailure("signup", "sign_up_failed", {
        error_name: err instanceof Error ? err.name : "unknown"
      });
      setError(err?.message || "Sign-up failed. Please try again.");
      if (err instanceof JoinLinkError && err.switchToSignIn) setMode("signin");
    } finally {
      setLoading(false);
    }
  }

  if (isCreator) {
    // Creator join flows (content reward link, checkout, onboarding) render a
    // centered card on the 420px creator column — creator-studios design, not
    // the brand two-panel. Sign-up is the default entry, with a clear sign-in
    // button below the form.
    const badge = isContentRewardJoin ? "Content Reward" : "For Creators";
    const heading =
    mode === "signup" ?
    "Create an account to continue" :
    mode === "forgot" ?
    "Reset your password" :
    "Welcome back";
    const subtitle =
    mode === "signup" ?
    "Create your account to get access to content rewards" :
    mode === "forgot" ?
    "Enter your email and we'll send you a reset link." :
    "Sign in to your creator account.";

    return (
      <div className="crl-page" data-page-id="login" data-hydrated={hydrated}>
        <div className="crl-card">
          <div className="crl-logo">
            <img src={sfnLogo} alt="Short Form Nation" />
          </div>

          <span className="crl-badge">{badge}</span>
          <h1 className="crl-heading">{heading}</h1>
          <p className="crl-subtitle">{subtitle}</p>

          {error &&
          <div className="crl-error">
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd" />

              </svg>
              {error}
            </div>
          }

          {/* Sign-up form (default entry for creator join) */}
          {mode === "signup" &&
          <form onSubmit={handleEmailSignUp} className="crl-form">
              <div className="crl-field">
                <label htmlFor="signup-email" className="crl-label">
                  email address
                </label>
                <input
                id="signup-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                className="crl-input" />

              </div>
              <div className="crl-field">
                <label htmlFor="signup-password" className="crl-label">
                  password
                </label>
                <input
                id="signup-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="password"
                autoComplete="new-password"
                className="crl-input" />

              </div>
              <button type="submit" disabled={loading} className="crl-submit">
                {loading ? "Creating account…" : "Create account"}
              </button>
              <button
              type="button"
              onClick={() => switchMode("signin")}
              className="crl-ghost">

                Already have an account? Sign in
              </button>
            </form>
          }

          {/* Sign-in form */}
          {mode === "signin" &&
          <form onSubmit={handleEmailSignIn} className="crl-form">
              <div className="crl-field">
                <label htmlFor="email" className="crl-label">
                  email address
                </label>
                <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                className="crl-input" />

              </div>
              <div className="crl-field">
                <label htmlFor="password" className="crl-label">
                  password
                </label>
                <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="password"
                autoComplete="current-password"
                className="crl-input" />

              </div>
              <button type="submit" disabled={loading} className="crl-submit">
                {loading ? "Signing in…" : "Sign in"}
              </button>
              <button
              type="button"
              onClick={() => switchMode("forgot")}
              className="crl-text-btn">

                Forgot your password?
              </button>
              <button
              type="button"
              onClick={() => switchMode("signup")}
              className="crl-ghost">

                New here? Create an account
              </button>
            </form>
          }

          {/* Forgot-password form */}
          {mode === "forgot" && !forgotSent &&
          <form onSubmit={handleForgotPassword} className="crl-form">
              <div className="crl-field">
                <label htmlFor="forgot-email" className="crl-label">
                  email address
                </label>
                <input
                id="forgot-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                className="crl-input" />

              </div>
              <button type="submit" disabled={loading} className="crl-submit">
                {loading ? "Sending…" : "Send reset link"}
              </button>
              <button
              type="button"
              onClick={() => switchMode("signin")}
              className="crl-text-btn">

                ← Back to sign in
              </button>
            </form>
          }

          {mode === "forgot" && forgotSent &&
          <div className="crl-success">
              Check your inbox — we've sent a reset link to{" "}
              <strong>{email}</strong>.
            </div>
          }

          <p className="crl-footer">
            Need access?{" "}
            <a href="mailto:hello@shortformnation.com">Contact us</a>
          </p>
        </div>
      </div>);

  }

  return (
    <div className="flex min-h-screen w-full" data-page-id="login" data-hydrated={hydrated}>
      {/* ── Left panel: brand messaging ── */}
      <div
        className="hidden lg:flex lg:w-1/2 xl:w-3/5 flex-col justify-between p-12 xl:p-16"
        style={{ backgroundColor: "var(--sfn-black)" }}>

        {/* Logo */}
        <div>
          <img
            src={sfnLogo}
            alt="Short Form Nation"
            className="h-10 w-auto" />

        </div>

        {/* Hero copy */}
        <div className="flex flex-col gap-8 max-w-xl">
          <div>
            <span
              className="inline-block text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-6"
              style={{
                backgroundColor: "rgba(199, 245, 155, 0.15)",
                color: "var(--sfn-lime)"
              }}>

              {copy.badge}
            </span>
            <h1
              className="text-4xl xl:text-5xl font-bold leading-tight"
              style={{ color: "var(--sfn-white)" }}>

              {copy.heading}
            </h1>
            <p className="mt-5 text-lg leading-relaxed" style={{ color: "#999999" }}>
              {copy.subheading}
            </p>
          </div>

          {/* Value props */}
          <ul className="flex flex-col gap-4">
            {copy.valueProps.map((item) =>
            <li key={item.title} className="flex items-start gap-4">
                <span className="text-xl mt-0.5">{item.icon}</span>
                <div>
                  <p className="font-semibold text-sm" style={{ color: "var(--sfn-white)" }}>
                    {item.title}
                  </p>
                  <p className="text-sm mt-0.5" style={{ color: "#666666" }}>
                    {item.desc}
                  </p>
                </div>
              </li>
            )}
          </ul>
        </div>

        {/* Footer note */}
        {/* suppressHydrationWarning: the year can legitimately differ between
             server render and client hydration at a year boundary. */}
        <p className="text-xs" style={{ color: "#404040" }} suppressHydrationWarning>
          © {new Date().getFullYear()} Short Form Nation. All rights reserved.
        </p>
      </div>

      {/* ── Right panel: sign-in form ── */}
      <div
        className="flex w-full lg:w-1/2 xl:w-2/5 flex-col justify-center px-8 sm:px-16 xl:px-24"
        style={{ backgroundColor: "var(--sfn-white)" }}>

        {/* Mobile logo (only visible below lg) */}
        <div className="flex justify-center mb-10 lg:hidden">
          <img src={sfnLogo} alt="Short Form Nation" className="h-9 w-auto" />
        </div>

        <div className="w-full max-w-sm mx-auto">
          <h2
            className="text-2xl font-bold mb-1"
            style={{ color: "var(--sfn-black)" }}>

            {mode === "signup" ? "Set up your new account" : "Welcome back"}
          </h2>
          <p className="text-sm mb-6" style={{ color: "var(--sfn-steel)" }}>
            {copy.formSubtitle(mode)}
          </p>

          {/* Mode toggle — hidden in forgot-password mode */}
          {mode !== "forgot" &&
          <div
            className="flex rounded-lg p-1 mb-6"
            style={{ backgroundColor: "#f0f0f0" }}>

            {(["signin", "signup"] as const).map((m) =>
            <button
              key={m}
              type="button"
              onClick={() => switchMode(m)}
              className="flex-1 py-2 text-sm font-medium rounded-md transition-all"
              style={{
                backgroundColor: mode === m ? "var(--sfn-white)" : "transparent",
                color: mode === m ? "var(--sfn-black)" : "#999999",
                boxShadow: mode === m ? "0 1px 3px rgba(0,0,0,0.1)" : "none"
              }}>

                {m === "signin" ? "Sign in" : "Create account"}
              </button>
            )}
          </div>
          }

          {/* Error message */}
          {error &&
          <div
            className="flex items-start gap-2 text-sm px-4 py-3 rounded-lg mb-6"
            style={{
              backgroundColor: "#fff0eb",
              color: "var(--sfn-tangerine)",
              border: "1px solid #ffd0b8"
            }}>

              <svg
              className="w-4 h-4 shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20">

                <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd" />

              </svg>
              {error}
            </div>
          }

          {/* Sign-in form */}
          {mode === "signin" &&
          <form onSubmit={handleEmailSignIn} className="flex flex-col gap-5">
              <div>
                <label
                htmlFor="email"
                className="block text-sm font-medium mb-1.5"
                style={{ color: "var(--sfn-black)" }}>

                  Email address
                </label>
                <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                className="w-full px-4 py-2.5 text-sm rounded-lg border transition-colors focus:outline-none"
                style={{
                  borderColor: "#e5e5e5",
                  backgroundColor: "#fafafa",
                  color: "var(--sfn-black)"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "var(--sfn-tangerine)";
                  e.target.style.backgroundColor = "var(--sfn-white)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e5e5";
                  e.target.style.backgroundColor = "#fafafa";
                }} />

              </div>
              <div>
                <label
                htmlFor="password"
                className="block text-sm font-medium mb-1.5"
                style={{ color: "var(--sfn-black)" }}>

                  Password
                </label>
                <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="password"
                autoComplete="current-password"
                className="w-full px-4 py-2.5 text-sm rounded-lg border transition-colors focus:outline-none"
                style={{
                  borderColor: "#e5e5e5",
                  backgroundColor: "#fafafa",
                  color: "var(--sfn-black)"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "var(--sfn-tangerine)";
                  e.target.style.backgroundColor = "var(--sfn-white)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e5e5";
                  e.target.style.backgroundColor = "#fafafa";
                }} />

              </div>
              <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg text-sm font-semibold transition-opacity disabled:opacity-60"
              style={{
                backgroundColor: "var(--sfn-tangerine)",
                color: "var(--sfn-white)"
              }}
              onMouseEnter={(e) => {
                if (!loading)
                (e.target as HTMLButtonElement).style.backgroundColor =
                "var(--sfn-tangerine-light)";
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLButtonElement).style.backgroundColor =
                "var(--sfn-tangerine)";
              }}>

                {loading ? "Signing in…" : "Sign in"}
              </button>
              <button
              type="button"
              onClick={() => switchMode("forgot")}
              className="w-full text-center text-sm mt-1"
              style={{ color: "var(--sfn-steel)", background: "none", border: "none", cursor: "pointer" }}>

                Forgot your password?
              </button>
            </form>
          }

          {/* Forgot password form */}
          {mode === "forgot" && !forgotSent &&
          <form onSubmit={handleForgotPassword} className="flex flex-col gap-5">
              <p className="text-sm" style={{ color: "var(--sfn-steel)" }}>
                Enter your email and we'll send you a reset link.
              </p>
              <div>
                <label
                htmlFor="forgot-email"
                className="block text-sm font-medium mb-1.5"
                style={{ color: "var(--sfn-black)" }}>

                  Email address
                </label>
                <input
                id="forgot-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                className="w-full px-4 py-2.5 text-sm rounded-lg border transition-colors focus:outline-none"
                style={{ borderColor: "#e5e5e5", backgroundColor: "#fafafa", color: "var(--sfn-black)" }}
                onFocus={(e) => {
                  e.target.style.borderColor = "var(--sfn-tangerine)";
                  e.target.style.backgroundColor = "var(--sfn-white)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e5e5";
                  e.target.style.backgroundColor = "#fafafa";
                }} />

              </div>
              <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg text-sm font-semibold transition-opacity disabled:opacity-60"
              style={{ backgroundColor: "var(--sfn-tangerine)", color: "var(--sfn-white)" }}>

                {loading ? "Sending…" : "Send reset link"}
              </button>
              <button
              type="button"
              onClick={() => switchMode("signin")}
              className="w-full text-center text-sm"
              style={{ color: "var(--sfn-steel)", background: "none", border: "none", cursor: "pointer" }}>

                ← Back to sign in
              </button>
            </form>
          }

          {/* Forgot password success state */}
          {mode === "forgot" && forgotSent &&
          <div className="flex flex-col gap-4">
              <div
              className="text-sm px-4 py-3 rounded-lg"
              style={{ backgroundColor: "#f0faf0", color: "#2d7a2d", border: "1px solid #b8ddb8" }}>

                Check your inbox — we've sent a reset link to <strong>{email}</strong>.
              </div>
              <button
              type="button"
              onClick={() => switchMode("signin")}
              className="w-full text-center text-sm"
              style={{ color: "var(--sfn-steel)", background: "none", border: "none", cursor: "pointer" }}>

                ← Back to sign in
              </button>
            </div>
          }

          {/* Sign-up form */}
          {mode === "signup" &&
          <form onSubmit={handleEmailSignUp} className="flex flex-col gap-5">
              <div>
                <label
                htmlFor="name"
                className="block text-sm font-medium mb-1.5"
                style={{ color: "var(--sfn-black)" }}>

                  Full name
                </label>
                <input
                id="name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Smith"
                autoComplete="name"
                className="w-full px-4 py-2.5 text-sm rounded-lg border transition-colors focus:outline-none"
                style={{
                  borderColor: "#e5e5e5",
                  backgroundColor: "#fafafa",
                  color: "var(--sfn-black)"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "var(--sfn-tangerine)";
                  e.target.style.backgroundColor = "var(--sfn-white)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e5e5";
                  e.target.style.backgroundColor = "#fafafa";
                }} />

              </div>
              <div>
                <label
                htmlFor="signup-email"
                className="block text-sm font-medium mb-1.5"
                style={{ color: "var(--sfn-black)" }}>

                  Email address
                </label>
                <input
                id="signup-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                className="w-full px-4 py-2.5 text-sm rounded-lg border transition-colors focus:outline-none"
                style={{
                  borderColor: "#e5e5e5",
                  backgroundColor: "#fafafa",
                  color: "var(--sfn-black)"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "var(--sfn-tangerine)";
                  e.target.style.backgroundColor = "var(--sfn-white)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e5e5";
                  e.target.style.backgroundColor = "#fafafa";
                }} />

              </div>
              <div>
                <label
                htmlFor="signup-password"
                className="block text-sm font-medium mb-1.5"
                style={{ color: "var(--sfn-black)" }}>

                  Password
                </label>
                <input
                id="signup-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="password"
                autoComplete="new-password"
                className="w-full px-4 py-2.5 text-sm rounded-lg border transition-colors focus:outline-none"
                style={{
                  borderColor: "#e5e5e5",
                  backgroundColor: "#fafafa",
                  color: "var(--sfn-black)"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "var(--sfn-tangerine)";
                  e.target.style.backgroundColor = "var(--sfn-white)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e5e5";
                  e.target.style.backgroundColor = "#fafafa";
                }} />

              </div>
              <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg text-sm font-semibold transition-opacity disabled:opacity-60"
              style={{
                backgroundColor: "var(--sfn-tangerine)",
                color: "var(--sfn-white)"
              }}
              onMouseEnter={(e) => {
                if (!loading)
                (e.target as HTMLButtonElement).style.backgroundColor =
                "var(--sfn-tangerine-light)";
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLButtonElement).style.backgroundColor =
                "var(--sfn-tangerine)";
              }}>

                {loading ? "Creating account…" : "Create account"}
              </button>
            </form>
          }

          <p className="mt-8 text-xs text-center" style={{ color: "#999999" }}>
            Need access?{" "}
            <a
              href="mailto:hello@shortformnation.com"
              className="underline underline-offset-2"
              style={{ color: "var(--sfn-tangerine)" }}>

              Contact us
            </a>
          </p>
        </div>
      </div>
    </div>);

}